import styled, { keyframes } from "styled-components";

export const PageContainer = styled.div`
  padding: 16px 24px;
  background: #f5f5f5;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

export const PageTitle = styled.h2`
  font-weight: 700;
  font-size: 18px;
  color: #1f5454;
  margin: 0;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

export const StorageCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 13px;
  color: #444;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  strong {
    color: #1f5454;
  }

  svg {
    flex-shrink: 0;
  }
`;

export const ExitLink = styled.span`
  color: #f21167;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const ExitFooter = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: center;
  padding: 32px 0 20px;
`;

export const ExitInwardButton = styled.button`
  min-width: 380px;
  padding: 26px 72px;
  background: #ef4e23;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(239, 78, 35, 0.35);
  transition: background 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    background: #d8431c;
    box-shadow: 0 8px 20px rgba(239, 78, 35, 0.45);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const GridContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 16px;
`;

export const PendingOrdersBanner = styled.div<{ $error?: boolean }>`
  margin: -4px 0 16px;
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid ${({ $error }) => ($error ? "#ffccc7" : "#b7eb8f")};
  background: ${({ $error }) => ($error ? "#fff2f0" : "#f6ffed")};
  color: ${({ $error }) => ($error ? "#cf1322" : "#1f5454")};
  font-size: 13px;
  font-weight: 600;
`;

export const ScanSkuCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
`;

export const ScanSkuInputRow = styled.div`
  width: 100%;
  max-width: 420px;
`;

export const ScanSkuHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1f5454;
  }

  span {
    color: #666;
    font-size: 13px;
  }
`;

export const ScanStatus = styled.span<{ $error?: boolean }>`
  color: ${({ $error }) => ($error ? "#cf1322" : "#1f5454")};
  font-size: 13px;
  font-weight: ${({ $error }) => ($error ? 600 : 400)};
`;

export const SlotsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1f5454;
`;

export const SlotsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

export const SlotsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SlotCard = styled.div<{ state?: string }>`
  display: flex;
  flex-direction: column;
  width: 140px;
  min-width: 140px;
  height: 180px;
  flex-shrink: 0;
  border: 2px dashed
    ${(props) => {
      switch (props.state) {
        case "processing":
          return "#E7D84D";
        case "done":
          return "#07A568";
        case "disabled":
          return "#c0c0c0";
        default:
          return "#b0b0b0";
      }
    }};
  border-radius: 8px;
  background: ${(props) => {
    switch (props.state) {
      case "processing":
        return "#fffbe6";
      case "done":
        return "#f0fff4";
      case "disabled":
        return "#f5f5f5";
      default:
        return "#f8f8f8";
    }
  }};
  cursor: ${(props) =>
    props.state === "enabled" ? "pointer" : "default"};
  transition: all 0.2s ease;
  overflow: hidden;

  ${(props) =>
    props.state === "enabled" &&
    `
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
      border-color: #888;
    }
  `}

  .slot-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 8px;
  }

  .slot-footer {
    padding: 8px;
    background: ${(props) => {
      switch (props.state) {
        case "processing":
          return "#f5e6a0";
        case "done":
          return "#b7eb8f";
        case "disabled":
          return "#e0e0e0";
        default:
          return "#e8e8e8";
      }
    }};
    border-top: 1px solid
      ${(props) => {
        switch (props.state) {
          case "processing":
            return "#e8d54e";
          case "done":
            return "#95de64";
          case "disabled":
            return "#c6c6c6";
          default:
            return "#d0d0d0";
        }
      }};
    text-align: center;
    font-size: 13px;
    font-weight: 700;
    color: #333;
  }

  .processing-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: #b8860b;
    font-weight: 600;
    font-size: 13px;
  }

  .done-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .done-indicator .tick {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #07a568;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    font-weight: bold;
    animation: scaleIn 0.3s ease;
  }

  @keyframes scaleIn {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .enabled-indicator {
    color: #999;
    font-size: 13px;
    font-weight: 500;
  }

  .disabled-indicator {
    color: #aaa;
    font-size: 13px;
    font-style: italic;
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #f5e6a0;
  border-top: 3px solid #d4a900;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const LargeSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #d9d9d9;
  border-top: 4px solid #4d9a9a;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  height: 50vh;
  color: #666;
  font-size: 15px;
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 50vh;
  color: #888;
  font-size: 15px;
`;

/* Keep for backward compatibility with any external imports */
export const Container = styled.div``;
